import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAccessTiers } from '../../../hooks/usePremiumAccess';

const { FiPlus, FiEdit2, FiTrash2, FiShield, FiLoader, FiX, FiCheck } = FiIcons;

const COLORS = ['#6366f1', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#06b6d4'];

const TierForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || { name: '', description: '', color: '#6366f1', rank: 0 });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    await onSave(form);
    setLoading(false);
  };

  return (
    <div className="p-5 bg-zinc-900 border border-zinc-700 rounded-2xl space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Tier Name *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-white font-bold placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
            placeholder="e.g. Premium, VIP..." />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Rank (order)</label>
          <input type="number" value={form.rank} onChange={e => set('rank', parseInt(e.target.value) || 0)}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-white font-bold focus:outline-none focus:border-zinc-600" />
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Description</label>
        <input value={form.description} onChange={e => set('description', e.target.value)}
          className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-white font-bold placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
          placeholder="What does this tier unlock?" />
      </div>
      <div className="space-y-1.5">
        <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Color</label>
        <div className="flex gap-2 flex-wrap">
          {COLORS.map(c => (
            <button key={c} onClick={() => set('color', c)}
              className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c ? 'border-white scale-110' : 'border-transparent'}`}
              style={{ backgroundColor: c }} />
          ))}
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 border border-zinc-700 rounded-xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:border-zinc-500 transition-all">
          Cancel
        </button>
        <button onClick={handleSave} disabled={loading || !form.name.trim()}
          className="flex items-center gap-2 px-5 py-2 bg-white text-black text-xs font-black uppercase tracking-widest rounded-xl hover:scale-105 transition-all disabled:opacity-50">
          {loading && <SafeIcon icon={FiLoader} className="animate-spin text-xs" />}
          Save Tier
        </button>
      </div>
    </div>
  );
};

const AccessTierManager = ({ artistId }) => {
  const { tiers, loading, createTier, updateTier, deleteTier } = useAccessTiers(artistId);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleCreate = async (form) => {
    await createTier(form);
    setShowForm(false);
  };

  const handleUpdate = async (id, form) => {
    await updateTier(id, form);
    setEditingId(null);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-10">
      <div className="w-6 h-6 border-2 border-zinc-700 border-t-white rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-black uppercase tracking-widest text-white">Access Tiers</h3>
          <p className="text-[10px] font-bold text-zinc-600 mt-0.5">Named access levels you can assign to content</p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-xl text-[10px] font-black uppercase tracking-widest text-zinc-300 hover:border-zinc-600 transition-all">
            <SafeIcon icon={FiPlus} className="text-xs" /> Add Tier
          </button>
        )}
      </div>

      {showForm && (
        <TierForm onSave={handleCreate} onCancel={() => setShowForm(false)} />
      )}

      {tiers.length === 0 && !showForm && (
        <div className="text-center py-8 border border-dashed border-zinc-800 rounded-2xl">
          <SafeIcon icon={FiShield} className="text-2xl text-zinc-700 mx-auto mb-2" />
          <p className="text-xs font-black uppercase tracking-widest text-zinc-600">No access tiers yet</p>
          <p className="text-[10px] font-bold text-zinc-700 mt-1">Create tiers like "Premium" or "VIP" to restrict content</p>
        </div>
      )}

      <div className="space-y-2">
        {tiers.map(tier => (
          <div key={tier.id}>
            {editingId === tier.id ? (
              <TierForm initial={tier} onSave={(f) => handleUpdate(tier.id, f)} onCancel={() => setEditingId(null)} />
            ) : (
              <div className="flex items-center gap-3 p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl group">
                <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: tier.color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-white uppercase tracking-tight">{tier.name}</p>
                  {tier.description && <p className="text-[10px] font-bold text-zinc-600 truncate">{tier.description}</p>}
                </div>
                <span className="text-[9px] font-black uppercase tracking-widest text-zinc-700 border border-zinc-800 px-2 py-0.5 rounded-full">
                  rank {tier.rank}
                </span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setEditingId(tier.id)}
                    className="p-1.5 rounded-lg border border-zinc-700 text-zinc-500 hover:text-zinc-300 transition-all">
                    <SafeIcon icon={FiEdit2} className="text-xs" />
                  </button>
                  {confirmDelete === tier.id ? (
                    <>
                      <button onClick={async () => { await deleteTier(tier.id); setConfirmDelete(null); }}
                        className="p-1.5 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-all">
                        <SafeIcon icon={FiCheck} className="text-xs" />
                      </button>
                      <button onClick={() => setConfirmDelete(null)}
                        className="p-1.5 rounded-lg border border-zinc-700 text-zinc-500 transition-all">
                        <SafeIcon icon={FiX} className="text-xs" />
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmDelete(tier.id)}
                      className="p-1.5 rounded-lg border border-zinc-700 text-zinc-500 hover:text-red-400 hover:border-red-500/40 transition-all">
                      <SafeIcon icon={FiTrash2} className="text-xs" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AccessTierManager;